import { Injectable } from '@angular/core';
import {
  Actions,
  createEffect,
  ofType
} from '@ngrx/effects';
import { delay, filter, map, take } from 'rxjs/operators';
import * as WsActions from './websocket.actions';
import { WsService } from '../../services/ws.service';
import * as SiteActions from '../site/site.actions';
import { ToastrService } from 'ngx-toastr';
import { combineLatest, of, switchMap, tap } from 'rxjs';
import { SiteStatus } from '../../enums/site';
import { TranslocoService } from '@jsverse/transloco';
import { Deserialize } from 'cerialize';
import { selectCurrentSite, selectSiteById } from '../site/site.selectors';
import { Store } from '@ngrx/store';
import { WebsocketTools } from '../../tools/websocket.tools';
import { selectUser } from '../user/user.selectors';
import { Comment } from '../../models/comment';
import { TrackerType } from '../../enums/tracker';
import { concatLatestFrom } from '@ngrx/operators';
import { Site } from '../../models/site';
import { DifferentialTileset } from '../../models/differential-tileset';
import * as TilesetConfigActions
  from '../tileset-config/tileset-config.actions';
import {
  DifferentialTilesetTools
} from '../../tools/differential-tileset.tools';
import { TilesetConfig } from '../../models/tileset-config';
import { DatePipe } from '@angular/common';
import {
  selectDetectionLabelEntities
} from '../detection-label/detection-label.selectors';

@Injectable()
export class WsEffects {

  private getCommentTargetName(
    comment: Comment,
    detectionLabels: Record<string, any>,
  ): string {
    switch (comment.targetType) {
      case 'tracker':
        if (comment.targetName) {
          return comment.targetName;
        }

        const key =
          comment.trackerType === TrackerType.IMAGE
            ? 'marker'
            : 'elevation';

        return (
          this.translocoService.translate(key)
          + ' '
          + comment.targetId.slice(0, 4).toUpperCase()
        );

      case 'detection_pool': {
        const label =
          detectionLabels?.[comment.targetName]?.label
          ?? comment.targetName;

        return (
          label
          + ' '
          + comment.targetId.slice(0, 4).toUpperCase()
        );
      }

      default:
        return comment.targetName
          ? comment.targetName
          : this.translocoService.translate(comment.targetType)
          + ' '
          + comment.targetId.slice(0, 4).toUpperCase();
    }
  }

  private eventActionMap: Record<string, (message: any) => any> = {
    new_comment: (message) =>
      WsActions.newCommentReceived({
        comment: message.payload,
        topic: message.topic!
      }),

    update_site: (message) =>
      WsActions.updateSiteReceived({
        site: message.payload,
        topic: message.topic!
      }),

    update_differential_tileset: (message) =>
      WsActions.updateDifferentialTilesetReceived({
        differentialTileset: message.payload,
        topic: message.topic!
      }),

    site_structure_stop: (message) =>
      WsActions.siteStructureStopReceived({
        site: message.payload,
        topic: message.topic!
      }),

    site_structure_error: (message) =>
      WsActions.siteStructureErrorReceived({
        site: message.payload,
        topic: message.topic!
      }),

    warn_structure_before_stop: (message) =>
      WsActions.warnStructureBeforeStopReceived({
        topic: message.topic!
      }),
  };

  private static updateSiteFromWs(site: any) {
    return SiteActions.updateSite({
      update: {
        id: site.id,
        changes: {
          ...site
        }
      }
    });
  }

  private static updateTilesetConfigFromWs(tilesetConfig: any) {
    return TilesetConfigActions.updateTilesetConfig({
      update: {
        id: tilesetConfig.id,
        changes: {
          ...tilesetConfig
        }
      }
    });
  }

  routeWsEvents$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WsActions.wsMessageReceived),
      filter(({ message }) => message.type === 'event'),
      map(({ message }) => {
        const actionFactory = this.eventActionMap[message.event!];
        return actionFactory
          ? actionFactory(message)
          : { type: '[WS] Noop' };
      })
    )
  );

  updateSite$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WsActions.updateSiteReceived),

      map(({ site }) => ({
        site: Deserialize(site, Site)
      })),

      concatLatestFrom(({ site }) =>
        this.store.select(selectSiteById(site.id))
      ),

      tap(([{ site }, currentSite]) => {
        if (!currentSite || currentSite.status === site.status) {
          return;
        }

        switch (site.status) {
          case SiteStatus.RUNNING:
            this.toaster.success(
              this.translocoService.translate(
                'advanced_features_available_for',
                {
                  site: site.name,
                }
              )
            );
            break;
        }
      }),

      map(([{ site }]) =>
        WsEffects.updateSiteFromWs(site)
      )
    )
  );

  updateDifferentialTileset$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WsActions.updateDifferentialTilesetReceived),

      map(({ differentialTileset, topic }) => ({
        differentialTileset: Deserialize(
          differentialTileset,
          DifferentialTileset
        ),
        siteId:
          WebsocketTools.parseTopicParts(topic)?.site ?? '',
      })),

      switchMap(({ differentialTileset, siteId }) =>
        combineLatest([
          this.store.select(selectCurrentSite),
          this.store.select(selectSiteById(siteId)),
        ]).pipe(
          take(1),
          map(([currentSite, site]) => ({
            differentialTileset,
            currentSite,
            site,
          }))
        )
      ),

      filter(
        (
          value
        ): value is {
          differentialTileset: DifferentialTileset;
          currentSite: Site;
          site: Site;
        } =>
          !!value.currentSite &&
          !!value.site &&
          value.currentSite.id === value.site.id
      ),

      map(({ differentialTileset, site }) => ({
        differentialTileset,
        site,
        tilesUrl:
          DifferentialTilesetTools.tilesetUrl(
            site,
            differentialTileset
          ),
      })),

      tap(({ differentialTileset }) => {
        const activeLang =
          this.translocoService.getActiveLang();

        const datePipe: DatePipe =
          new DatePipe(activeLang);

        const dateFormat =
          activeLang.startsWith('fr')
            ? 'dd/MM/yyyy'
            : 'MM/dd/yyyy';

        const message =
          this.translocoService.translate(
            'differential_tileset_updated',
            {
              compared_date: datePipe.transform(
                new Date(
                  differentialTileset.batchComparedDate
                ),
                dateFormat
              ),
              reference_date: datePipe.transform(
                new Date(
                  differentialTileset.batchReferenceDate
                ),
                dateFormat
              )
            }
          );

        this.toaster.success(message);
      }),

      map(({ differentialTileset, tilesUrl }) => {
        const tilesetConfig = new TilesetConfig(
          differentialTileset.id,
          differentialTileset.batchReferenceId,
          true,
          differentialTileset.maxDistance,
          tilesUrl,
        );

        return WsEffects.updateTilesetConfigFromWs(
          tilesetConfig
        );
      })
    )
  );

  siteStructureStop$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WsActions.siteStructureStopReceived),

      map(({ site }) => ({
        site: Deserialize(site, Site)
      })),

      concatLatestFrom(({ site }) =>
        this.store.select(selectSiteById(site.id))
      ),

      tap(([{ site }, currentSite]) => {
        if (!currentSite || currentSite.status === site.status) {
          return;
        }

        this.toaster.success(
          this.translocoService.translate(
            'advanced_features_deactivated_for',
            {
              site: site.name,
            }
          )
        );
      }),

      map(([{ site }]) =>
        WsEffects.updateSiteFromWs(site)
      )
    )
  );

  siteStructureError$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WsActions.siteStructureErrorReceived),

      map(({ site }) => ({
        site: Deserialize(site, Site)
      })),

      concatLatestFrom(({ site }) =>
        this.store.select(selectSiteById(site.id))
      ),

      tap(([{ site }, currentSite]) => {
        if (!currentSite || currentSite.status === site.status) {
          return;
        }

        this.toaster.error(
          this.translocoService.translate(
            'advanced_features_activation_error_for',
            {
              site: site.name,
            }
          )
        );
      }),

      map(([{ site }]) =>
        WsEffects.updateSiteFromWs(site)
      )
    )
  );

  newComment$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(WsActions.newCommentReceived),

        map(({ comment, topic }) => ({
          comment: Deserialize(comment, Comment),
          siteId:
            WebsocketTools.parseTopicParts(topic)?.site ?? '',
        })),

        switchMap(({ comment, siteId }) =>
          combineLatest([
            this.store.select(selectSiteById(siteId)),
            this.store.select(selectUser),
            this.store.select(
              selectDetectionLabelEntities
            ),
          ]).pipe(
            take(1),
            map(([
                   site,
                   user,
                   detectionLabels,
                 ]) => ({
              comment,
              site,
              user,
              detectionLabels,
            }))
          )
        ),

        tap(({
               comment,
               site,
               user,
               detectionLabels,
             }) => {
          const targetName =
            this.getCommentTargetName(
              comment,
              detectionLabels
            );

          if (
            !site ||
            !user ||
            !targetName ||
            comment.author === user.name
          ) {
            return;
          }

          const message =
            this.translocoService.translate(
              'new_comment',
              {
                author: comment.author,
                item: targetName,
                site: site.name,
              }
            );

          this.toaster.info(message);
        })
      ),
    { dispatch: false }
  );

  warnStructureBeforeStop = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          WsActions.warnStructureBeforeStopReceived
        ),

        map(({ topic }) => ({
          siteId:
            WebsocketTools.parseTopicParts(topic)?.site ?? '',
        })),

        switchMap(({ siteId }) =>
          combineLatest([
            this.store.select(
              selectSiteById(siteId)
            ),
            this.store.select(selectUser),
          ]).pipe(
            take(1),
            map(([site, user]) => ({
              site,
              user
            }))
          )
        ),

        tap(({ site, user }) => {
          if (!site || !user) {
            return;
          }

          const message =
            this.translocoService.translate(
              'warn_structure_before_stop',
              {
                site: site.name
              }
            );

          this.toaster.warning(message);
        })
      ),
    { dispatch: false }
  );

  incomingMessages$ = createEffect(() =>
    this.wsService.incomingMessages$.pipe(
      map((message) =>
        WsActions.wsMessageReceived({ message })
      )
    )
  );

  connected$ = createEffect(() =>
    this.wsService.connected$.pipe(
      map(() => WsActions.wsConnected())
    )
  );

  disconnected$ = createEffect(() =>
    this.wsService.disconnected$.pipe(
      map((event) =>
        WsActions.wsDisconnected({ event })
      )
    )
  );

  reconnectOnDisconnect$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(WsActions.wsDisconnected),
        delay(2000),
        tap(() => {
          this.wsService.reconnect();
        })
      ),
    { dispatch: false }
  );

  constructor(
    private store: Store,
    private actions$: Actions,
    private wsService: WsService,
    private toaster: ToastrService,
    private translocoService: TranslocoService
  ) {}
}
